import { LogManager } from '@src/utility/Logger';

import { registerTtfeConsoleCommands } from './ttfeConsole';

/**
 * Dev override for the local-canvas size limit (`requiresNativeLocalPreview`):
 * uploads at or under it are pushed into the renderer iframe, larger ones
 * play through the native `<video>` preview. Persisted in localStorage so it
 * survives a reload; read on every check, but the canvas path is chosen once
 * per story, so set it before opening (or uploading) the story.
 *
 *   __ttfe.setMaxLocalAssetMb(50)    // files over 50 MB take the native path
 *   __ttfe.setMaxLocalAssetMb(null)  // back to the env / default limit
 *   __ttfe.maxLocalAssetMb()         // current override, or null
 */
export const LOCAL_ASSET_LIMIT_OVERRIDE_KEY = 'ttfe.maxRendererLocalAssetMb';

const logger = LogManager.getLogger('ttfe:local-asset-limit');

/** The override in megabytes, or null when unset or invalid. */
export function getLocalAssetLimitOverrideMb(): number | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(LOCAL_ASSET_LIMIT_OVERRIDE_KEY);
    if (raw == null) return null;
    const mb = Number(raw);
    return Number.isFinite(mb) && mb >= 0 ? mb : null;
  } catch {
    return null;
  }
}

registerTtfeConsoleCommands({
  setMaxLocalAssetMb: (mb: number | null) => {
    try {
      if (mb == null) {
        localStorage.removeItem(LOCAL_ASSET_LIMIT_OVERRIDE_KEY);
        logger.info('Local asset limit override cleared');
        return;
      }
      if (!Number.isFinite(mb) || mb < 0) {
        logger.warn('Expected a size in MB (>= 0) or null', { mb });
        return;
      }
      localStorage.setItem(LOCAL_ASSET_LIMIT_OVERRIDE_KEY, String(mb));
      logger.info(
        'Local asset limit override set; applies to the next story opened',
        { mb },
      );
    } catch (error) {
      logger.warn('Could not write the override to localStorage', { error });
    }
  },
  maxLocalAssetMb: () => getLocalAssetLimitOverrideMb(),
});
